import LegalLayout from "@/components/LegalLayout";
import { useDocumentMeta } from "../lib/useDocumentMeta";
import { SEO_ROUTES } from "../lib/seo";

const SECTIONS = [
  {
    heading: "Product Orders & Fulfillment",
    body: [
      "BASEPOINT purchases are currently completed through eBay. When you buy through our listing, eBay provides us with order and delivery information needed to fulfill your purchase. We use those details, your product and color requests, and order messages to ship your order, provide support, and maintain order records.",
      "eBay handles marketplace checkout under its own privacy policy and transaction terms. ForgedOps does not receive your full payment card details from that checkout. Shipping providers receive the delivery information needed to ship your order. Separately purchased software or services may use Stripe for payment processing and our email provider for service communications.",
    ],
  },

  {
    heading: "Overview",
    body: [
      "This Privacy Policy explains how ForgedOps LLC (“ForgedOps,” “we,” “our”) collects, uses, and protects information when you visit our website, request a consultation, or engage with our proprietary operational platforms, white-labeled deployments, and training programs (collectively, the “ForgedOps Services”).",
      "ForgedOps treats operational data with the same discipline we apply to the field — controlled, accountable, and protected.",
    ],
  },
  {
    heading: "Information We Collect",
    body: [
      "We collect only the information necessary to evaluate engagements, deliver services, and operate our proprietary platforms responsibly.",
    ],
    bullets: [
      "Contact details you submit (name, email, phone, company, role, project context)",
      "Engagement details required to scope a consulting, training, or platform deployment",
      "Operational data captured through ForgedOps proprietary platforms during the term of an active engagement (governed separately by the applicable Engagement Agreement)",
      "Standard website analytics (pages visited, referring source, device type) used to improve performance and content",
    ],
  },
  {
    heading: "How We Use Information",
    body: [
      "Information is used to: (a) respond to consultation requests and prepare proposals; (b) deliver consulting, training, and platform services; (c) operate, secure, and improve ForgedOps proprietary infrastructure; (d) communicate operationally relevant updates; and (e) comply with applicable legal obligations.",
      "ForgedOps does not sell customer operational data. Aggregated, anonymized operational benchmarks may be used internally to refine field-tested workflows and platform capabilities.",
    ],
  },
  {
    heading: "Proprietary Platform Data",
    body: [
      "Customer field data, crew records, safety documentation, inspection records, incident reports, and operational reporting captured through ForgedOps proprietary platforms remain the customer’s data. Access, retention, and use are governed by the applicable Engagement Agreement and any white-label / customer-branded deployment configuration.",
      "Platform infrastructure, workflow logic, and architectural designs underlying these deployments remain proprietary to ForgedOps LLC.",
    ],
  },
  {
    heading: "How We Share Information",
    body: [
      "We share information only as necessary to deliver our services and operate our infrastructure. This may include:",
    ],
    bullets: [
      "Service providers under written confidentiality and data-protection obligations (hosting, analytics, communications, payment processing)",
      "Customer-authorized integrations and platform connections specified in an Engagement Agreement",
      "Compliance with applicable legal process, regulatory requirements, or to protect ForgedOps rights and operations",
    ],
  },
  {
    heading: "Security",
    body: [
      "ForgedOps applies administrative, technical, and physical safeguards to protect proprietary infrastructure and customer data. No system is perfectly secure, however, and we encourage customers to follow applicable platform access controls and crew onboarding protocols.",
    ],
  },
  {
    heading: "Cookies & Analytics",
    body: [
      "This website may use cookies and similar technologies to remember preferences, measure performance, and improve user experience. You can control cookies through your browser. Disabling cookies may limit some functionality.",
    ],
  },
  {
    heading: "Your Choices",
    body: [
      "You may request access to, correction of, or deletion of personal information you have submitted through this website by contacting our operations team. Where deletion would conflict with an active Engagement Agreement, retention obligations under that agreement will control.",
    ],
  },
  {
    heading: "Third-Party Links",
    body: [
      "The ForgedOps website may link to third-party sites, integrations, or downloadable resources. We are not responsible for third-party privacy practices. Review the privacy policies of any third-party site you visit.",
    ],
  },
  {
    heading: "Children's Privacy",
    body: [
      "The ForgedOps Services are intended for businesses, contractors, and operational personnel. The site is not directed to children under 16, and we do not knowingly collect personal information from children.",
    ],
  },
  {
    heading: "Updates to This Policy",
    body: [
      "We may update this Privacy Policy from time to time. Material changes will be reflected in the “Last updated” date above. Continued use of the ForgedOps Services after changes constitutes acceptance.",
    ],
  },
  {
    heading: "Contact",
    body: [
      "Questions about this Privacy Policy, data subject requests, or proprietary platform data inquiries can be directed to our operations team through the Contact page.",
    ],
  },
];

export default function Privacy() {
  useDocumentMeta(SEO_ROUTES["/privacy"]);
  return (
    <LegalLayout
      testid="privacy-page"
      kicker="Legal · Operational"
      title={
        <>
          Privacy <span className="metallic-text">Policy</span>
        </>
      }
      lastUpdated="October 22, 2026"
      intro="ForgedOps LLC operates proprietary operational platforms and field-tested infrastructure for heavy civil and infrastructure contractors. This Privacy Policy explains how we handle information across our website and proprietary systems."
      sections={SECTIONS}
    />
  );
}
